import React, {
  type FC,
  Fragment,
  type ReactElement,
  useEffect,
  useState,
} from "react";
import { FaChevronRight, FaUser } from "react-icons/fa";
import { FaBars } from "react-icons/fa6";
import { OutboundLink } from "gatsby-plugin-google-gtag";
import { Link, useI18next } from "gatsby-plugin-react-i18next";

import CurrenciesModal from "@components/currencies_modal";
import AboutIcon from "@components/icons/about_icon";
import CurrencyIcon from "@components/icons/currency_icon";
import LangIcon from "@components/icons/language_icon";
import LogoutIcon from "@components/icons/logout_icon";
import MoreIcon from "@components/icons/more_icon";
import PartnershipIcon from "@components/icons/partnership_icon";
import ServicesIcon from "@components/icons/services_icon";
import StoreIcon from "@components/icons/store_icon";
import LangModal from "@components/lang_modal";
import LogoutModal from "@components/logout_modal";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@components/navigation-menu";
import ProfileBubbleSection from "@components/profile_bubble_section";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@components/sheet";
import { useAffiliate } from "@hooks/affiliate";
import { useAccountDetailState } from "@stores/account_detail";
import { useAuthenticationState } from "@stores/authentication";
import { getAffiliateLogoUrl } from "@utilities/affiliate";
import { getProfileUrl } from "@utilities/base-url";
import { cn, defaultRipple, primaryRipple } from "@utilities/helper";
import { AnimatePresence, motion } from "motion/react";

type NavKey = "shop" | "services" | "partnership" | "about" | "more";
interface NavGroup {
  title: string;
  items: NavItem[];
}
interface NavItem {
  type: "internal" | "external";
  href: string;
  label: string;
}

type NavSection = {
  key: NavKey;
  title: string;
  logo: ReactElement;
} & (
  | {
      type: "links";
      items: NavItem[];
      isExpanded: boolean;
      setIsExpanded: (expanded: boolean) => void;
    }
  | {
      type: "groups";
      groups: NavGroup[];
      isExpanded: boolean;
      setIsExpanded: (expanded: boolean) => void;
    }
  | { type: "link"; href: string }
);

const NavLinkItem: FC<{ item: NavItem; className?: string }> = ({
  item,
  className,
}) => {
  if (item.type === "internal") {
    return (
      <Link to={item.href} className={className}>
        {item.label}
      </Link>
    );
  }
  return (
    <OutboundLink href={item.href} className={className}>
      {item.label}
    </OutboundLink>
  );
};

const NavMobileLinkItem: FC<{ item: NavItem; className?: string }> = ({
  item,
  className,
}) => {
  if (item.type === "internal") {
    return (
      <SheetClose asChild>
        <Link to={item.href} className={className}>
          {item.label}
        </Link>
      </SheetClose>
    );
  }
  return (
    <SheetClose asChild>
      <OutboundLink href={item.href} className={className}>
        {item.label}
      </OutboundLink>
    </SheetClose>
  );
};

const AuthLink: FC<{
  type: "both" | "login" | "register";
}> = ({ type = "both" }): ReactElement => {
  const { t } = useI18next();

  switch (type) {
    case "login":
      return (
        <Link
          ref={primaryRipple}
          className="custom-link-btn-primary flex cursor-pointer items-center gap-1 justify-self-center sm:gap-2"
          to="/login"
          placeholder={undefined}
          onPointerEnterCapture={undefined}
          onPointerLeaveCapture={undefined}
        >
          <FaUser className="size-3 text-primary" />
          <span className="text-start text-sm">
            {t("signup") + "/" + t("login_title")}
          </span>
        </Link>
      );
    case "register":
      return (
        <Link
          ref={primaryRipple}
          className="custom-link-btn-primary flex cursor-pointer items-center gap-1 justify-self-center sm:gap-2"
          to="/register"
          placeholder={undefined}
          onPointerEnterCapture={undefined}
          onPointerLeaveCapture={undefined}
        >
          <FaUser className="size-3 text-primary" />
          <span className="text-start text-sm">
            {t("signup") + "/" + t("login_title")}
          </span>
        </Link>
      );
    case "both":
    default:
      return (
        <div
          ref={primaryRipple}
          className="flex rounded-full px-4 py-2 transition duration-300 hover:bg-primary-100"
        >
          <Link
            className="flex cursor-pointer gap-1 justify-self-end text-primary no-underline focus:outline-none sm:gap-2 md:items-center"
            to="/register"
            placeholder={undefined}
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          >
            <FaUser className="size-3 text-primary" />
            <span className="text-start text-sm">{t("signup") + "/"}</span>
          </Link>
          <Link
            className="flex gap-1 justify-self-start text-primary no-underline focus:outline-none sm:gap-2"
            to="/login"
            placeholder={undefined}
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          >
            <span className="text-start text-sm">{t("login_title")}</span>
          </Link>
        </div>
      );
  }
};

const AccordionNavMobileItem: FC<{
  title: string;
  logo: ReactElement;
  items: NavItem[];
  isExpanded: boolean;
  onToggle: () => void;
}> = ({ title, logo, items, isExpanded, onToggle }): ReactElement => (
  <div
    className={
      "focus-within:custom-primary-focus-ring flex flex-col rounded-md !border-2 border-solid border-primary-200 transition duration-300 hover:border-primary"
    }
  >
    <button
      className="flex flex-row items-center justify-between gap-4 rounded-md border-2 border-transparent p-3 outline-none transition duration-300 hover:text-primary focus:text-primary"
      onClick={onToggle}
    >
      <div className="flex flex-row items-center gap-4">
        {logo}
        {title}
      </div>
      <FaChevronRight
        className={cn(
          "size-4 opacity-80 transition duration-300 rtl:-scale-x-100",
          {
            "rotate-90 rtl:-rotate-90": isExpanded,
          },
        )}
      />
    </button>
    <motion.div
      className="grid overflow-hidden transition-[grid-template-rows] duration-300"
      initial={{ gridTemplateRows: "0fr" }}
      animate={{
        gridTemplateRows: isExpanded ? "1fr" : "0fr",
      }}
      transition={{ duration: 0.3 }}
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{
          opacity: isExpanded ? 1 : 0,
        }}
        transition={{ duration: 0.3 }}
        className="min-h-0"
      >
        <div className="flex flex-col ps-4">
          {items.map((item, index) => (
            <NavMobileLinkItem
              key={index}
              item={item}
              className="custom-nav-mobile-sub-link"
            />
          ))}
        </div>
      </motion.div>
    </motion.div>
  </div>
);

const AccordionNavMobileGroup: FC<{
  title: string;
  logo: ReactElement;
  groups: NavGroup[];
  isExpanded: boolean;
  onToggle: () => void;
}> = ({ title, logo, groups, isExpanded, onToggle }): ReactElement => (
  <div
    className={
      "focus-within:custom-primary-focus-ring flex flex-col rounded-md !border-2 border-solid border-primary-200 transition duration-300 hover:border-primary"
    }
  >
    <button
      className="flex flex-row items-center justify-between gap-4 rounded-md border-2 border-transparent p-3 outline-none transition duration-300 hover:text-primary focus:text-primary"
      onClick={onToggle}
    >
      <div className="flex flex-row items-center gap-4">
        {logo}
        {title}
      </div>
      <FaChevronRight
        className={cn(
          "size-4 opacity-80 transition duration-300 rtl:-scale-x-100",
          {
            "rotate-90 rtl:-rotate-90": isExpanded,
          },
        )}
      />
    </button>
    <motion.div
      className="grid overflow-hidden transition-[grid-template-rows] duration-300"
      initial={{ gridTemplateRows: "0fr" }}
      animate={{
        gridTemplateRows: isExpanded ? "1fr" : "0fr",
      }}
      transition={{ duration: 0.3 }}
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{
          opacity: isExpanded ? 1 : 0,
        }}
        transition={{ duration: 0.3 }}
        className="min-h-0"
      >
        <div className="flex flex-col ps-4">
          {groups.map((group, groupIndex) => (
            <Fragment key={groupIndex}>
              <div className="font-semibold uppercase text-primary">
                {group.title}
              </div>
              <div className="flex flex-col">
                {group.items.map((item, itemIndex) => (
                  <NavMobileLinkItem
                    key={itemIndex}
                    item={item}
                    className="custom-nav-mobile-sub-link"
                  />
                ))}
              </div>
            </Fragment>
          ))}
        </div>
      </motion.div>
    </motion.div>
  </div>
);

const Header = (): ReactElement => {
  const accountDetailState = useAccountDetailState();
  // Empty for accounts that signed up with email + OTP only (no name step).
  const userName = (accountDetailState.license.name ?? "").trim();
  const authenticationState = useAuthenticationState();
  const { affiliate, isResolvingAffiliate } = useAffiliate();
  const affiliateLogoUrl = affiliate ? getAffiliateLogoUrl(affiliate) : "";

  const { t, language, navigate } = useI18next();

  const [langModalOpen, setLangModalOpen] = useState<boolean>(false);
  const [currencyModalOpen, setCurrencyModalOpen] = useState<boolean>(false);
  const [logoutModalOpen, setLogoutModalOpen] = useState<boolean>(false);

  const profileLink = getProfileUrl();

  const getGreeting = (): string => {
    const currentHour = new Date().getHours();

    if (currentHour >= 5 && currentHour < 12) {
      return t("greeting_0");
    } else if (currentHour >= 12 && currentHour < 18) {
      return t("greeting_1");
    } else {
      return t("greeting_2");
    }
  };

  const [isShopExpanded, setIsShopExpanded] = useState<boolean>(false);
  const [isServicesExpanded, setIsServicesExpanded] = useState<boolean>(false);
  const [isAboutExpanded, setIsAboutExpanded] = useState<boolean>(false);
  const [isMoreExpanded, setIsMoreExpanded] = useState<boolean>(false);

  const resetExpandedStates = (): void => {
    setIsShopExpanded(false);
    setIsServicesExpanded(false);
    setIsAboutExpanded(false);
    setIsMoreExpanded(false);
  };

  const navSections: NavSection[] = [
    {
      key: "shop",
      title: t("shop"),
      logo: <StoreIcon className="size-6" />,
      type: "links",
      items: [
        { type: "internal", href: "/", label: "eSIM" },
        {
          type: "internal",
          href: "/sim-cards",
          label: t("sim_card"),
        },
        {
          type: "internal",
          href: "/bne-go-portable-esim-wifi-router",
          label: "BNE Go Pocket WiFi",
        },
        {
          type: "internal",
          href: "/bne-guard-vpn",
          label: "BNE Guard VPN",
        },
      ],
      isExpanded: isShopExpanded,
      setIsExpanded: setIsShopExpanded,
    },
    {
      key: "services",
      title: t("services"),
      logo: <ServicesIcon className="size-6" />,
      type: "groups",
      groups: [
        {
          title: t("mobile_data"),
          items: [
            {
              type: "internal",
              href: "/why-bne-travel-esim/",
              label: t("why_bne_esim"),
            },
            {
              type: "internal",
              href: "/why-bne-sim-card/",
              label: t("why_bne_sim_card"),
            },
            {
              type: "internal",
              href: "/manage-your-esim-and-sim-card-with-the-bnesim-app/",
              label: t("manage_esim_and_sim_card_with_bne_app"),
            },
            {
              type: "internal",
              href: "/sharing-esim/",
              label: t("sharing_esim"),
            },
            // {
            //   type: "external",
            //   href: "https://www.bnesim.com/free-whatsapp-texting-esim",
            //   label: t("esim_with_free_whatsapp_texting"),
            // },
          ],
        },
        {
          title: t("for_professionals"),
          items: [
            {
              type: "external",
              href: "https://www.bnesim.com/bne-pro/",
              label: "BNE Pro",
            },
            {
              type: "internal",
              href: "/bne-enterprise/",
              label: "BNE Enterprise",
            },
            {
              type: "internal",
              href: "/iot-sim/",
              label: "IoT",
            },
          ],
        },
        {
          title: t("call_and_phone_numbers"),
          items: [
            {
              type: "external",
              href: "https://www.bnesim.com/voice/",
              label: t("worldwide_calls"),
            },
            {
              type: "internal",
              href: "/dialer/",
              label: t("dialer"),
            },
            {
              type: "internal",
              href: "/bne-room/",
              label: "BNE Room",
            },
            {
              type: "external",
              href: "https://www.bnesim.com/international-phone-numbers/",
              label: t("international_phone_numbers"),
            },
          ],
        },
      ],
      isExpanded: isServicesExpanded,
      setIsExpanded: setIsServicesExpanded,
    },
    {
      key: "partnership",
      title: t("partnership"),
      logo: <PartnershipIcon className="size-6" />,
      type: "link",
      href: "/partner-with-bnesim/",
    },
    {
      key: "about",
      title: t("about"),
      logo: <AboutIcon className="size-6" />,
      type: "links",
      items: [
        {
          type: "internal",
          href: "/about-bnesim/",
          label: t("about_us"),
        },
        {
          type: "internal",
          href: "/contacts/",
          label: t("contact_us"),
        },
        {
          type: "external",
          href: "https://www.bnesim.com/help/",
          label: t("help_center"),
        },
        {
          type: "external",
          href: "https://www.bnesim.com/press-release/",
          label: t("press_release"),
        },
      ],
      isExpanded: isAboutExpanded,
      setIsExpanded: setIsAboutExpanded,
    },
    {
      key: "more",
      title: t("more"),
      logo: <MoreIcon className="size-6" />,
      type: "links",
      items: [
        {
          type: "internal",
          href: "/offers/",
          label: t("offers"),
        },
        {
          type: "internal",
          href: "/bne-wallet/",
          label: t("wallet"),
        },
        {
          type: "internal",
          href: "/refer-and-earn/",
          label: t("refer_and_earn"),
        },
        {
          type: "external",
          href: "https://www.bnesim.com/category/all/",
          label: t("blog"),
        },
      ],
      isExpanded: isMoreExpanded,
      setIsExpanded: setIsMoreExpanded,
    },
  ];

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1200);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <header className="fixed inset-x-0 top-0 z-20 flex w-screen justify-center border-b-2 border-gray-300 bg-white">
      <nav className="container flex min-h-[75px] w-full items-center justify-between">
        <div className="flex items-center justify-between md:justify-start">
          {isResolvingAffiliate ? (
            <div
              className="h-[27px] w-[117px] shrink-0 animate-pulse rounded-md bg-gray-200 md:h-[33px] md:w-[143px]"
              aria-hidden
            />
          ) : affiliate !== null ? (
            <OutboundLink
              href={affiliate.website || "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="h-fit shrink-0 text-primary-100 transition duration-300 hover:opacity-80 focus:opacity-80 focus:outline-none"
            >
              <img
                src={affiliateLogoUrl || "/images/bnesim_logo.svg"}
                className="h-[27px] w-auto md:h-[33px]"
                loading="eager"
                width={300}
                height={69}
                alt={`${affiliate.name} logo`}
              />
            </OutboundLink>
          ) : (
            <Link
              to="/"
              className="h-fit shrink-0 text-primary-100 transition duration-300 hover:opacity-80 focus:opacity-80 focus:outline-none"
            >
              <img
                src="/images/bnesim_logo.svg"
                className="h-[27px] w-auto md:h-[33px]"
                loading="eager"
                width={300}
                height={69}
                alt="Official BNESIM Logo – A sleek modern design representing the world’s best travel eSIM provider for global connectivity and seamless communication."
              />
            </Link>
          )}
        </div>
        <div className="hidden flex-row items-center gap-4 sm:flex md:gap-6">
          <NavigationMenu viewport={isMobile} className="hidden lg:flex">
            <NavigationMenuList className="flex-wrap">
              {navSections.map(section => {
                switch (section.type) {
                  case "links":
                    return (
                      <NavigationMenuItem key={section.key}>
                        <NavigationMenuTrigger>
                          {section.title}
                        </NavigationMenuTrigger>
                        <NavigationMenuContent>
                          <div className="flex flex-col">
                            {section.items.map((item, index) => (
                              <NavLinkItem
                                key={index}
                                item={item}
                                className="custom-nav-menu-link"
                              />
                            ))}
                          </div>
                        </NavigationMenuContent>
                      </NavigationMenuItem>
                    );
                  case "groups":
                    return (
                      <NavigationMenuItem key={section.key}>
                        <NavigationMenuTrigger>
                          {section.title}
                        </NavigationMenuTrigger>
                        <NavigationMenuContent>
                          <div className="flex flex-row justify-center">
                            {section.groups.map((group, groupIndex) => (
                              <div
                                key={groupIndex}
                                className="custom-nav-menu-group"
                              >
                                <span className="custom-nav-menu-header">
                                  {group.title}
                                </span>
                                <div className="flex flex-col ps-2">
                                  {group.items.map((item, itemIndex) => (
                                    <NavLinkItem
                                      key={itemIndex}
                                      item={item}
                                      className="custom-nav-menu-link"
                                    />
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </NavigationMenuContent>
                      </NavigationMenuItem>
                    );
                  case "link":
                    return (
                      <NavigationMenuItem key={section.key}>
                        <Link
                          to={section.href}
                          className={cn(
                            "custom-link-btn-primary",
                            "group inline-flex w-max items-center gap-1 text-sm font-normal text-black hover:text-primary sm:gap-2",
                          )}
                        >
                          {section.title}
                        </Link>
                      </NavigationMenuItem>
                    );
                  default:
                    return null;
                }
              })}
            </NavigationMenuList>
          </NavigationMenu>
        </div>
        <div className="flex items-center gap-1 sm:gap-2 md:gap-4">
          <AnimatePresence>
            {!authenticationState.isAuthenticated() && (
              <motion.div
                initial={{ opacity: 0.8 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="hidden flex-row items-center gap-1 sm:gap-2 md:gap-4 lg:flex"
              >
                <AuthLink type="register" />
                <button
                  ref={primaryRipple}
                  onClick={() => {
                    setLangModalOpen(true);
                  }}
                  className="custom-link-btn-primary flex items-center gap-1 justify-self-end sm:gap-2 md:px-4"
                >
                  <LangIcon className="size-3" />
                  <span className="text-sm">{language.toUpperCase()}</span>
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          <Sheet>
            <SheetTrigger asChild>
              <button
                onClick={resetExpandedStates}
                className={cn(
                  "group h-fit text-primary-100 no-underline transition duration-300 hover:opacity-80 focus:opacity-80 focus:outline-none",
                  {
                    "flex lg:hidden": !authenticationState.isAuthenticated(),
                  },
                )}
              >
                <div className="flex flex-row items-center gap-2 sm:gap-4">
                  {authenticationState.isAuthenticated() && (
                    <div className="flex flex-col justify-start">
                      <span className="text-nowrap text-end text-base font-semibold text-black transition duration-300 group-hover:text-primary group-focus:text-primary">
                        {userName !== "" ? `${t("hi")} ${userName}` : t("hi")}
                      </span>
                      <span className="text-nowrap text-end text-sm text-black opacity-80 transition duration-300 group-hover:text-primary group-focus:text-primary">
                        {getGreeting()}
                      </span>
                    </div>
                  )}
                  <FaBars className="size-4 text-black transition duration-300 group-hover:text-primary group-focus:text-primary" />
                </div>
              </button>
            </SheetTrigger>
            <SheetContent>
              <SheetHeader>
                <SheetTitle className="sr-only">{t("menu")}</SheetTitle>
                {authenticationState.isAuthenticated() && (
                  <ProfileBubbleSection />
                )}
              </SheetHeader>
              <div className="-mx-6 -my-2 grid flex-1 auto-rows-min gap-4 overflow-auto px-6 py-2">
                {authenticationState.isAuthenticated() ? (
                  <button
                    ref={defaultRipple}
                    className="custom-nav-mobile-primary-link"
                    onClick={() => {
                      // Never hand an unset-password session to
                      // app.bnesim.com — its cookie auto-login would leave
                      // the user stranded on a half-registered account.
                      if (authenticationState.is_password_changed === 0) {
                        void navigate("/complete-account");
                        return;
                      }
                      if (typeof window !== "undefined") {
                        window.location.href = profileLink;
                      }
                    }}
                  >
                    <div className="flex flex-row items-center gap-4">
                      <FaUser className="size-6" />
                      <span className="text-sm font-semibold">
                        {t("manage_your_account")}
                      </span>
                    </div>
                    <FaChevronRight className="size-4 opacity-80 transition duration-300 rtl:-scale-x-100" />
                  </button>
                ) : (
                  <motion.div
                    initial={{ opacity: 0.8 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="w-full pt-6"
                  >
                    <SheetClose asChild>
                      <button
                        ref={defaultRipple}
                        className="custom-nav-mobile-primary-link"
                        onClick={() => {
                          void navigate("/register");
                        }}
                      >
                        <div className="flex flex-row items-center gap-4">
                          <FaUser className="size-6" />
                          <span className="text-sm font-semibold">
                            {t("signup") + "/" + t("login_title")}
                          </span>
                        </div>
                        <FaChevronRight className="size-4 opacity-80 transition duration-300 rtl:-scale-x-100" />
                      </button>
                    </SheetClose>
                  </motion.div>
                )}
                <div className="contents lg:hidden">
                  {navSections.map(section => {
                    switch (section.type) {
                      case "link":
                        return (
                          <SheetClose key={section.key} asChild>
                            <Link
                              to={section.href}
                              className="custom-nav-mobile-item justify-between"
                            >
                              <div className="flex flex-row items-center gap-4">
                                {section.logo}
                                {section.title}
                              </div>
                              <FaChevronRight
                                className={cn(
                                  "size-4 opacity-80 transition duration-300 rtl:-scale-x-100",
                                )}
                              />
                            </Link>
                          </SheetClose>
                        );
                      case "links":
                        return (
                          <AccordionNavMobileItem
                            key={section.key}
                            title={section.title}
                            logo={section.logo}
                            items={section.items}
                            isExpanded={section.isExpanded}
                            onToggle={() => {
                              section.setIsExpanded(!section.isExpanded);
                            }}
                          />
                        );
                      case "groups":
                        return (
                          <AccordionNavMobileGroup
                            key={section.key}
                            title={section.title}
                            logo={section.logo}
                            groups={section.groups}
                            isExpanded={section.isExpanded}
                            onToggle={() => {
                              section.setIsExpanded(!section.isExpanded);
                            }}
                          />
                        );
                      default:
                        return null;
                    }
                  })}
                </div>
                <SheetClose asChild>
                  <button
                    ref={primaryRipple}
                    className="custom-nav-mobile-item"
                    onClick={() => {
                      setLangModalOpen(true);
                    }}
                  >
                    <LangIcon className="size-6" />
                    <span className="text-sm">{t("language")}</span>
                  </button>
                </SheetClose>
                <SheetClose asChild>
                  <button
                    ref={primaryRipple}
                    className="custom-nav-mobile-item"
                    onClick={() => {
                      setCurrencyModalOpen(true);
                    }}
                  >
                    <CurrencyIcon className="size-6" />
                    <span className="text-sm">{t("currency")}</span>
                  </button>
                </SheetClose>
              </div>
              {authenticationState.isAuthenticated() && (
                <SheetFooter>
                  <div className="flex w-full flex-col">
                    <SheetClose asChild>
                      <button
                        ref={primaryRipple}
                        className={cn(
                          "custom-nav-mobile-item",
                          "hover:text-red-500",
                        )}
                        onClick={() => {
                          setLogoutModalOpen(true);
                        }}
                      >
                        <LogoutIcon className="size-6" />
                        <span className="text-sm">{t("logout")}</span>
                      </button>
                    </SheetClose>
                  </div>
                </SheetFooter>
              )}
            </SheetContent>
          </Sheet>
        </div>
      </nav>
      <LogoutModal
        isModalOpen={logoutModalOpen}
        setIsModalOpen={setLogoutModalOpen}
      />
      <LangModal
        isModalOpen={langModalOpen}
        setIsModalOpen={setLangModalOpen}
      />
      <CurrenciesModal
        isModalOpen={currencyModalOpen}
        setIsModalOpen={setCurrencyModalOpen}
      />
    </header>
  );
};

export default Header;
